import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import {
  cardById,
  catalogueSections,
  groupLabels,
  selectableCards,
  sourceLinks,
} from './data/investitureData';
import { groundedPremises } from './data/groundedPremises';
import { scenarioFrames } from './data/scenarioFrames';
import {
  formatScenarioForSharing,
  generateScenario,
} from './scenarioEngine';

const EXCLUSION_STORAGE_KEY = 'investiture-randomizer-exclusions-v2';
const LEGACY_STORAGE_KEY = 'investiture-randomizer-profile-v1';

const worldFilters = [
  { id: 'all', label: 'All worlds' },
  { id: 'scadrial', label: 'Scadrial' },
  { id: 'roshar', label: 'Roshar' },
];

const systemFilters = [
  { id: 'all', label: 'All systems', world: 'all' },
  { id: 'character', label: 'Character types', world: 'all' },
  { id: 'allomancy', label: 'Allomancy', world: 'scadrial' },
  { id: 'feruchemy', label: 'Feruchemy', world: 'scadrial' },
  { id: 'surgebinding', label: 'Surges', world: 'roshar' },
  { id: 'radiant-orders', label: 'Radiant Orders', world: 'roshar' },
];

const toneOptions = [
  { id: 'balanced', label: 'Balanced' },
  { id: 'tactical', label: 'Tactical' },
  { id: 'moral', label: 'Moral dilemma' },
  { id: 'chaotic', label: 'Chaotic' },
];

const scenarioKinds = [
  { id: 'canon', label: 'Canon', description: 'Set on Scadrial or Roshar' },
  { id: 'earth', label: 'Earth', description: 'Set in a familiar Earth setting' },
];

function loadExcludedCards() {
  try {
    const storedExclusions = JSON.parse(window.localStorage.getItem(EXCLUSION_STORAGE_KEY));
    if (Array.isArray(storedExclusions)) {
      return new Set(storedExclusions.filter((id) => cardById[id] && !cardById[id].disabled));
    }

    const legacySelections = JSON.parse(window.localStorage.getItem(LEGACY_STORAGE_KEY));
    if (Array.isArray(legacySelections) && legacySelections.length > 0) {
      const selected = new Set(legacySelections);
      return new Set(
        selectableCards.filter((card) => !selected.has(card.id)).map((card) => card.id)
      );
    }
  } catch {
    return new Set();
  }

  return new Set();
}

function getSearchText(card) {
  return [
    card.name,
    card.subtitle,
    card.category,
    card.summary,
    card.details,
    ...(card.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function CardArtwork({ card, compact = false }) {
  return (
    <span className={`card-artwork${compact ? ' card-artwork--compact' : ''}`} aria-hidden="true">
      {card.images.map((image, index) => (
        <span className="card-symbol-shell" key={image}>
          <img src={image} alt="" className="card-symbol" />
          {card.imageLabels?.[index] && (
            <span className="card-symbol-label">{card.imageLabels[index]}</span>
          )}
        </span>
      ))}
    </span>
  );
}

function SelectionCard({ card, enabled, onToggle, onInfo }) {
  const systemLabel =
    card.group === 'character' ? card.subtitle : `${groupLabels[card.group]}: ${card.subtitle}`;

  return (
    <article
      className={`selection-card selection-card--${card.world}${enabled ? ' is-selected' : ''}${
        card.disabled ? ' is-disabled' : ''
      }`}
    >
      <button
        className="selection-card__main"
        type="button"
        aria-pressed={enabled}
        aria-label={`${enabled ? 'Exclude' : 'Include'} ${card.name} ${groupLabels[card.group]}`}
        onClick={() => onToggle(card.id)}
        disabled={card.disabled}
      >
        <span className="selection-card__check" aria-hidden="true" />
        <CardArtwork card={card} />
        <span className="selection-card__copy">
          <span className="selection-card__name">{card.name}</span>
          <span className="selection-card__subtitle">{systemLabel}</span>
          <span className="selection-card__summary">{card.summary}</span>
        </span>
        {card.disabled && <span className="selection-card__status">Deferred</span>}
      </button>
      <button
        className="info-button"
        type="button"
        aria-label={`Learn about ${card.name} ${groupLabels[card.group]}`}
        onClick={() => onInfo(card)}
      >
        i
      </button>
    </article>
  );
}

function CardSection({ section, enabledIds, onToggle, onEnableAll, onDisableAll, onInfo }) {
  const selectable = section.cards.filter((card) => !card.disabled);
  const enabledCount = selectable.filter((card) => enabledIds.has(card.id)).length;
  const allEnabled = selectable.length > 0 && enabledCount === selectable.length;

  return (
    <section className="catalogue-section" aria-labelledby={`${section.id}-heading`}>
      <div className="section-heading-row">
        <div>
          <span className="section-eyebrow">{section.eyebrow}</span>
          <h2 id={`${section.id}-heading`}>{section.title}</h2>
          <p>{section.description}</p>
        </div>
        <div className="section-actions" aria-label={`${section.title} pool controls`}>
          <span className="section-count">{enabledCount} enabled</span>
          <button type="button" onClick={() => onEnableAll(selectable)} disabled={allEnabled}>
            Enable visible
          </button>
          <button type="button" onClick={() => onDisableAll(selectable)} disabled={enabledCount === 0}>
            Disable visible
          </button>
        </div>
      </div>
      <div className="card-grid">
        {section.cards.map((card) => (
          <SelectionCard
            key={card.id}
            card={card}
            enabled={enabledIds.has(card.id)}
            onToggle={onToggle}
            onInfo={onInfo}
          />
        ))}
      </div>
    </section>
  );
}

function PoolPanel({ enabledCards, excludedCards, onInclude, onReset }) {
  const worldCounts = enabledCards.reduce(
    (counts, card) => ({ ...counts, [card.world]: counts[card.world] + 1 }),
    { scadrial: 0, roshar: 0 }
  );

  const groupStats = Object.keys(groupLabels).map((group) => ({
    group,
    label: groupLabels[group],
    enabled: enabledCards.filter((card) => card.group === group).length,
    total: selectableCards.filter((card) => card.group === group).length,
  }));

  return (
    <aside className="profile-panel" aria-labelledby="pool-heading">
      <div className="profile-panel__header">
        <div>
          <span className="section-eyebrow">Generation filters</span>
          <h2 id="pool-heading">Active pool</h2>
        </div>
        <span className="profile-total" aria-label={`${enabledCards.length} enabled options`}>
          {enabledCards.length}
        </span>
      </div>

      <div className="world-totals" aria-label="Enabled options by world">
        <span><strong>{worldCounts.scadrial}</strong> Scadrial</span>
        <span><strong>{worldCounts.roshar}</strong> Roshar</span>
      </div>

      <div className="pool-stats">
        {groupStats.map((stat) => (
          <div className="pool-stat" key={stat.group}>
            <span>{stat.label}</span>
            <strong>{stat.enabled}/{stat.total}</strong>
          </div>
        ))}
      </div>

      <div className="excluded-list">
        <div className="profile-group__heading">
          <h3>Excluded</h3>
          <span>{excludedCards.length}</span>
        </div>
        {excludedCards.length === 0 ? (
          <p>Everything currently available can be generated.</p>
        ) : (
          <div className="profile-chips">
            {excludedCards.map((card) => (
              <button
                type="button"
                className={`profile-chip profile-chip--${card.world}`}
                key={card.id}
                onClick={() => onInclude(card.id)}
                aria-label={`Include ${card.name} again`}
              >
                {card.name}
                <span aria-hidden="true">+</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        className="clear-profile-button"
        type="button"
        onClick={onReset}
        disabled={excludedCards.length === 0}
      >
        Reset to everything
      </button>
      <div className="profile-status">
        <span className="profile-status__dot" aria-hidden="true" />
        New catalog entries join the default pool automatically.
      </div>
    </aside>
  );
}

function GeneratorPanel({ scenarioKind, mode, tone, crossover, onScenarioKind, onMode, onTone, onCrossover, onGenerate, error }) {
  const baseFrames = (scenarioKind === 'earth' ? groundedPremises : scenarioFrames)
    .map((entry) => entry.pressures.length)
    .reduce((total, variants) => total + variants, 0);

  return (
    <section className="generator-panel" aria-labelledby="generator-heading">
      <div className="generator-panel__copy">
        <span className="section-eyebrow">Scenario workshop</span>
        <h1 id="generator-heading">What would you do?</h1>
        <p>
          Generate a power-relevant problem with pressure, human stakes, and no single correct answer.
        </p>
        <span className="generator-library-count">
          {baseFrames.toLocaleString()} narrative frames before character combinations
        </span>
      </div>

      <div className="generator-controls">
        <fieldset>
          <legend>Scenario setting</legend>
          <div className="mode-control">
            {scenarioKinds.map((kind) => (
              <button
                type="button"
                className={scenarioKind === kind.id ? 'is-active' : ''}
                aria-pressed={scenarioKind === kind.id}
                onClick={() => onScenarioKind(kind.id)}
                key={kind.id}
                title={kind.description}
              >
                {kind.label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Players</legend>
          <div className="mode-control">
            <button
              type="button"
              className={mode === 'solo' ? 'is-active' : ''}
              aria-pressed={mode === 'solo'}
              onClick={() => onMode('solo')}
            >
              Solo
            </button>
            <button
              type="button"
              className={mode === 'pair' ? 'is-active' : ''}
              aria-pressed={mode === 'pair'}
              onClick={() => onMode('pair')}
            >
              Two players
            </button>
          </div>
        </fieldset>

        <label className="generator-field">
          <span>Tone</span>
          <select value={tone} onChange={(event) => onTone(event.target.value)}>
            {toneOptions.map((option) => (
              <option value={option.id} key={option.id}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="crossover-toggle">
          <input
            type="checkbox"
            checked={crossover}
            onChange={(event) => onCrossover(event.target.checked)}
          />
          <span>
            <strong>Crossover</strong>
            Allow characters from another world
          </span>
        </label>

        <button className="generate-button" type="button" onClick={onGenerate}>
          Generate scenario
        </button>
        {error && <p className="generator-error" role="alert">{error}</p>}
      </div>
    </section>
  );
}

function ResourceMeter({ reserve }) {
  return (
    <div className="resource-meter">
      <span>{reserve.label}</span>
      <strong>{reserve.amount}</strong>
      <small>{reserve.detail}</small>
    </div>
  );
}

function CharacterPortrait({ actor }) {
  if (!actor.portrait) return null;

  if (actor.portrait.spritePosition) {
    return (
      <div
        className="character-portrait character-portrait--sprite"
        role="img"
        aria-label={actor.portrait.label}
        style={{
          backgroundImage: `url(${actor.portrait.src})`,
          backgroundPosition: actor.portrait.spritePosition,
        }}
      />
    );
  }

  return (
    <img
      className="character-portrait"
      src={actor.portrait.src}
      alt={actor.portrait.label}
    />
  );
}

function ReserveInventory({ actor }) {
  const shouldCollapse = actor.reserves.length > 4;
  const visibleReserves = shouldCollapse ? actor.reserves.slice(0, 1) : actor.reserves;
  const additionalReserves = shouldCollapse ? actor.reserves.slice(1) : [];

  return (
    <div className="resource-list" aria-label={`${actor.displayName} reserves`}>
      {visibleReserves.map((reserve) => (
        <ResourceMeter reserve={reserve} key={`${reserve.kind}-${reserve.label}`} />
      ))}
      {additionalReserves.length > 0 && (
        <details className="additional-reserves">
          <summary>Show {additionalReserves.length} additional metal reserves</summary>
          <div>
            {additionalReserves.map((reserve) => (
              <ResourceMeter reserve={reserve} key={`${reserve.kind}-${reserve.label}`} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function InvestedAbilityCards({ actor, onInfo }) {
  const abilityCards = actor.sourceCardIds
    .map((id) => cardById[id])
    .filter((card) => card && ['allomancy', 'feruchemy', 'surgebinding'].includes(card.system));
  const compact = abilityCards.length > 1;

  if (!abilityCards.length) return null;

  return (
    <div
      className={`ability-reference-list${compact ? ' ability-reference-list--compact' : ''}`}
      role="group"
      aria-label={`${actor.displayName} invested abilities`}
    >
      <span className="ability-reference-list__label">
        {abilityCards.length === 1 ? 'Invested ability' : 'Invested abilities'}
      </span>
      {abilityCards.map((card) => (
        <article
          className={`ability-reference ability-reference--${card.world}`}
          key={card.id}
        >
          <img src={card.images[0]} alt="" />
          <div>
            <strong>{card.name}</strong>
            <span>{card.subtitle}</span>
            <p>{card.summary}</p>
          </div>
          <button
            type="button"
            aria-label={`Learn about ${card.name} ${groupLabels[card.group]}`}
            onClick={() => onInfo(card)}
          >
            i
          </button>
        </article>
      ))}
    </div>
  );
}

function ScenarioResult({
  scenario,
  locks,
  copied,
  onToggleLock,
  onRegenerate,
  onRerollPressure,
  onCopy,
}) {
  const [equipmentInfo, setEquipmentInfo] = useState(null);
  const [abilityInfo, setAbilityInfo] = useState(null);
  const worldLabels = { scadrial: 'Scadrial', roshar: 'Roshar', earth: 'Earth' };
  const worldLabel = worldLabels[scenario.world];
  const toneLabel = toneOptions.find((tone) => tone.id === scenario.tone)?.label;

  return (
    <section className="scenario-result" aria-label="Generated scenario">
      <div className="scenario-result__header">
        <div className="scenario-meta">
          <span>{worldLabel}</span>
          <span>{scenario.mode === 'solo' ? 'Solo' : 'Two players'}</span>
          <span>{toneLabel}</span>
          {scenario.world === 'earth' && <span>Power origin: {worldLabels[scenario.characterWorld]}</span>}
          {scenario.crossover && <span>Crossover</span>}
        </div>
        <span className="scenario-seed">Seed {scenario.seed}</span>
      </div>

      <div className="scenario-result__body">
        <span className="section-eyebrow">Generated experience</span>
        <h2>{scenario.title}</h2>

        <div className="character-briefing__heading">
          <span className="section-eyebrow">Character briefing</span>
          <small>Capabilities and reserves at the start of this scenario</small>
        </div>

        <div className="scenario-cast" aria-label="Character roles">
          {scenario.cast.map((actor, index) => (
            <div className="scenario-actor" key={`${actor.displayName}-${index}`}>
              <span className="scenario-actor__number">
                {scenario.mode === 'solo' ? 'Character' : `Player ${index + 1}`}
              </span>
              <div className="scenario-actor__title">
                <h3>{actor.displayName}</h3>
                {actor.oathLevel && <span>{actor.oathLabel}</span>}
              </div>
              <CharacterPortrait actor={actor} />
              <p className="scenario-actor__context">{actor.contextLine}</p>
              <div className="scenario-power-tags">
                {actor.powerNames.map((power) => <span key={power}>{power}</span>)}
              </div>
              <p className="scenario-actor__ability">{actor.abilityLine}</p>
              <InvestedAbilityCards actor={actor} onInfo={setAbilityInfo} />
              <p className="scenario-actor__loadout">{actor.loadoutLine}</p>

              <ReserveInventory actor={actor} />

              <div className="equipment-list" aria-label={`${actor.displayName} equipment`}>
                {actor.equipment.map((item) => (
                  <div className="equipment-item" key={item.id}>
                    <img src={item.image} alt="" />
                    <span>{item.name}</span>
                    <button
                      type="button"
                      aria-label={`Learn about ${item.name}`}
                      onClick={() => setEquipmentInfo(item)}
                    >
                      i
                    </button>
                  </div>
                ))}
                {actor.equipment.length === 0 && (
                  <p className="equipment-empty">No oath-granted Blade or Plate at this Ideal.</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="scenario-budget-note">
          Reserve amounts are generated scenario constraints, not canonical burn-rate measurements.
        </p>

        {scenario.relationship && (
          <div className="relationship-line">
            <strong>Relationship</strong>
            <span>{scenario.relationship}</span>
          </div>
        )}

        <div className="scenario-prose">
          <div>
            <span>Situation</span>
            <p>{scenario.narrative.situation}</p>
          </div>
          <div>
            <span>Pressure</span>
            <p>{scenario.narrative.pressure}</p>
          </div>
        </div>

        <div className="scenario-decision">
          <span>The decision</span>
          <p>{scenario.narrative.question}</p>
        </div>

        {scenario.relevanceTags.length > 0 && (
          <div className="relevance-line">
            <span>Power hooks</span>
            {scenario.relevanceTags.map((tag) => <strong key={tag}>{tag}</strong>)}
          </div>
        )}
      </div>

      <div className="scenario-toolbar">
        <div className="lock-controls" aria-label="Generation locks">
          <label>
            <input
              type="checkbox"
              checked={locks.cast}
              onChange={() => onToggleLock('cast')}
            />
            Keep character{scenario.cast.length > 1 ? 's' : ''}
          </label>
          <label>
            <input
              type="checkbox"
              checked={locks.setting}
              onChange={() => onToggleLock('setting')}
            />
            Keep setting
          </label>
        </div>
        <div className="scenario-actions">
          <button type="button" onClick={onRerollPressure}>Reroll pressure</button>
          <button type="button" onClick={onCopy}>{copied ? 'Copied' : 'Copy scenario'}</button>
          <button className="scenario-actions__primary" type="button" onClick={onRegenerate}>
            New scenario
          </button>
        </div>
      </div>
      {equipmentInfo && (
        <EquipmentInfoModal item={equipmentInfo} onClose={() => setEquipmentInfo(null)} />
      )}
      {abilityInfo && <InfoModal card={abilityInfo} onClose={() => setAbilityInfo(null)} />}
    </section>
  );
}

function EquipmentInfoModal({ item, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="equipment-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="equipment-modal-title"
        aria-describedby="equipment-modal-description"
      >
        <button
          className="modal-close"
          type="button"
          aria-label="Close equipment information"
          onClick={onClose}
          ref={closeButtonRef}
        >
          x
        </button>
        <div className="equipment-modal__art">
          <img src={item.image} alt={`Illustration of ${item.name}`} />
        </div>
        <div className="equipment-modal__copy">
          <span className="section-eyebrow">Equipment reference</span>
          <h2 id="equipment-modal-title">{item.name}</h2>
          <p className="modal-subtitle">{item.summary}</p>
          <p id="equipment-modal-description">{item.details}</p>
          <a href={item.sourceUrl} target="_blank" rel="noreferrer">
            Read the Coppermind reference
          </a>
          <small>Equipment artwork is an original illustrative interpretation.</small>
        </div>
      </section>
    </div>
  );
}

function InfoModal({ card, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`info-modal info-modal--${card.world}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <button
          className="modal-close"
          type="button"
          aria-label="Close information"
          onClick={onClose}
          ref={closeButtonRef}
        >
          x
        </button>
        <div className="modal-art-column">
          <CardArtwork card={card} compact />
          <span className="modal-world">{card.world === 'scadrial' ? 'Scadrial' : 'Roshar'}</span>
        </div>
        <div className="modal-copy">
          <span className="section-eyebrow">{groupLabels[card.group]}</span>
          <h2 id="modal-title">{card.name}</h2>
          <p className="modal-subtitle">{card.subtitle}</p>
          {card.category && <p className="modal-category">{card.category}</p>}
          <p id="modal-description">{card.details}</p>
          <a href={card.sourceUrl} target="_blank" rel="noreferrer">
            Read the Coppermind reference
          </a>
        </div>
      </section>
    </div>
  );
}

function copyText(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
  return Promise.resolve();
}

function App() {
  const [excludedIds, setExcludedIds] = useState(loadExcludedCards);
  const [world, setWorld] = useState('all');
  const [system, setSystem] = useState('all');
  const [query, setQuery] = useState('');
  const [infoCard, setInfoCard] = useState(null);
  const [mode, setMode] = useState('solo');
  const [tone, setTone] = useState('balanced');
  const [scenarioKind, setScenarioKind] = useState('canon');
  const [crossover, setCrossover] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [generationError, setGenerationError] = useState('');
  const [locks, setLocks] = useState({ cast: false, setting: false });
  const [copied, setCopied] = useState(false);

  const enabledIds = useMemo(
    () => new Set(selectableCards.filter((card) => !excludedIds.has(card.id)).map((card) => card.id)),
    [excludedIds]
  );
  const enabledCards = useMemo(
    () => selectableCards.filter((card) => enabledIds.has(card.id)),
    [enabledIds]
  );
  const excludedCards = useMemo(
    () => selectableCards.filter((card) => excludedIds.has(card.id)),
    [excludedIds]
  );

  useEffect(() => {
    window.localStorage.setItem(EXCLUSION_STORAGE_KEY, JSON.stringify([...excludedIds]));
  }, [excludedIds]);

  useEffect(() => {
    const selectedSystem = systemFilters.find((item) => item.id === system);
    if (world !== 'all' && selectedSystem?.world !== 'all' && selectedSystem?.world !== world) {
      setSystem('all');
    }
  }, [system, world]);

  const visibleSystemFilters = systemFilters.filter(
    (item) => world === 'all' || item.world === 'all' || item.world === world
  );

  const filteredSections = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return catalogueSections
      .map((section) => ({
        ...section,
        cards: section.cards.filter((card) => {
          const matchesWorld = world === 'all' || card.world === world;
          const matchesSystem = system === 'all' || card.system === system;
          const matchesQuery = !normalizedQuery || getSearchText(card).includes(normalizedQuery);
          return matchesWorld && matchesSystem && matchesQuery;
        }),
      }))
      .filter((section) => section.cards.length > 0);
  }, [query, system, world]);

  const toggleCard = (id) => {
    const card = cardById[id];
    if (!card || card.disabled) return;
    setExcludedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const enableCards = (cards) => {
    setExcludedIds((current) => {
      const next = new Set(current);
      cards.forEach((card) => next.delete(card.id));
      return next;
    });
  };

  const disableCards = (cards) => {
    setExcludedIds((current) => {
      const next = new Set(current);
      cards.forEach((card) => {
        if (!card.disabled) next.add(card.id);
      });
      return next;
    });
  };

  const runGeneration = (preserve = []) => {
    try {
      const generated = generateScenario({
        enabledIds,
        mode,
        tone,
        scenarioKind,
        crossover,
        previous: scenario,
        preserve,
      });
      setScenario(generated);
      setGenerationError('');
      setCopied(false);
    } catch (error) {
      setGenerationError(error.message);
    }
  };

  const handleGenerate = () => {
    const canPreserve = scenario && scenario.mode === mode && scenario.crossover === crossover
      && scenario.scenarioKind === scenarioKind;
    const preserved = canPreserve
      ? Object.keys(locks).filter((key) => locks[key])
      : [];
    runGeneration(preserved);
  };

  const rerollPressure = () => {
    if (!scenario) return;
    runGeneration(['cast', 'setting', 'challenge', 'stakes', 'relationship']);
  };

  const handleCopy = async () => {
    if (!scenario) return;
    await copyText(formatScenarioForSharing(scenario));
    setCopied(true);
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__inner">
          <a className="wordmark" href="#top" aria-label="Investiture Randomizer home">
            <span className="wordmark__symbol" aria-hidden="true">IR</span>
            <span>Investiture Randomizer</span>
          </a>
          <span className="header-count">
            <strong>{enabledCards.length}</strong> options enabled
          </span>
        </div>
      </header>

      <main id="top">
        <GeneratorPanel
          mode={mode}
          tone={tone}
          scenarioKind={scenarioKind}
          crossover={crossover}
          onScenarioKind={setScenarioKind}
          onMode={setMode}
          onTone={setTone}
          onCrossover={setCrossover}
          onGenerate={handleGenerate}
          error={generationError}
        />

        {scenario && (
          <ScenarioResult
            scenario={scenario}
            locks={locks}
            copied={copied}
            onToggleLock={(key) => setLocks((current) => ({ ...current, [key]: !current[key] }))}
            onRegenerate={handleGenerate}
            onRerollPressure={rerollPressure}
            onCopy={handleCopy}
          />
        )}

        <details className="tune-pool" id="tune-pool">
          <summary>
            <span>
              <strong>Tune the possibility pool</strong>
              <small>Optional filters for worlds, powers, and character types</small>
            </span>
            <span className="tune-pool__count">{enabledCards.length}/{selectableCards.length} enabled</span>
          </summary>

          <div className="tune-pool__content">
            <section className="filter-bar" aria-label="Card filters">
              <div className="world-tabs" role="group" aria-label="Filter by world">
                {worldFilters.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={world === item.id ? 'is-active' : ''}
                    aria-pressed={world === item.id}
                    onClick={() => setWorld(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <label className="filter-field">
                <span>System</span>
                <select value={system} onChange={(event) => setSystem(event.target.value)}>
                  {visibleSystemFilters.map((item) => (
                    <option value={item.id} key={item.id}>{item.label}</option>
                  ))}
                </select>
              </label>
              <label className="filter-field filter-field--search">
                <span>Search cards</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Metal, power, Order..."
                />
              </label>
            </section>

            <div className="workspace-layout">
              <div className="catalogue">
                {filteredSections.length > 0 ? (
                  filteredSections.map((section) => (
                    <CardSection
                      key={section.id}
                      section={section}
                      enabledIds={enabledIds}
                      onToggle={toggleCard}
                      onInfo={setInfoCard}
                      onEnableAll={enableCards}
                      onDisableAll={disableCards}
                    />
                  ))
                ) : (
                  <div className="empty-results">
                    <h2>No cards match those filters.</h2>
                    <p>Try a different world, system, or search term.</p>
                    <button type="button" onClick={() => { setWorld('all'); setSystem('all'); setQuery(''); }}>
                      Reset filters
                    </button>
                  </div>
                )}
              </div>
              <PoolPanel
                enabledCards={enabledCards}
                excludedCards={excludedCards}
                onInclude={toggleCard}
                onReset={() => setExcludedIds(new Set())}
              />
            </div>
          </div>
        </details>

        <footer className="app-footer">
          <p>
            Lore summaries are adapted from the Coppermind. Symbols are credited to Isaac Stewart
            and the tracers named on their source file pages.
          </p>
          <nav aria-label="Lore sources">
            {sourceLinks.map((source) => (
              <a key={source.label} href={source.url} target="_blank" rel="noreferrer">
                {source.label}
              </a>
            ))}
          </nav>
        </footer>
      </main>

      {infoCard && <InfoModal card={infoCard} onClose={() => setInfoCard(null)} />}
    </div>
  );
}

export default App;
